import type { SoilSightCropDefaults } from "models";

export const COVER_CROP_MIX_TYPE = {
  NONE: "00000000-0000-0000-0000-000000000000",
  WINTER_SINGLE_SPECIES: "7664dd56-2b55-4b40-a7f3-5c2e1f19f6da", // catch_crop_green_cover
  WINTER_MULTI_SPECIES: "53076fca-35e1-4fd0-ad92-808b54c0dac5", // winter_catch_crop_mixtures
  SUMMER_SINGLE_SPECIES: "bb0c0d54-9936-4912-9495-e1100749e10b", // catch_crop_as_summer_crop_stubble_sowing
  SUMMER_MULTI_SPECIES: "cdbeaa49-2ca1-4122-9beb-be011a4749be", // summer_catch_crop_mixtures
} as const;

export type CoverCropMixType = (typeof COVER_CROP_MIX_TYPE)[keyof typeof COVER_CROP_MIX_TYPE];

export const SOIL_SIGHT_TILLAGE = {
  NONE: "00000000-0000-0000-0000-000000000000",
  CONVENTIONAL: "ca0ed295-ddfd-4b44-b1a6-58fee6b0f36c",
  REDUCED: "f0f605ba-c40b-46a3-ab94-6e5018a51cd5",
} as const;

export type SoilSightTillage = (typeof SOIL_SIGHT_TILLAGE)[keyof typeof SOIL_SIGHT_TILLAGE];

export type HelpFieldKey =
  | "crop"
  | "yield"
  | "sellingPrice"
  | "minFertiliser"
  | "plantProtection"
  | "seedCosts"
  | "seedAmount"
  | "fodderLosses"
  | "mouldLosses"
  | "tillageId"
  | "residue"
  | "irrigation"
  | "coverCropId"
  | "legumeShare"
  | "ccCost";

export const GUIDANCE_KEY_MAP: Record<HelpFieldKey, string> = {
  crop: "crop",
  yield: "yield",
  sellingPrice: "sellingPrice",
  minFertiliser: "minFertiliser",
  plantProtection: "plantProtection",
  seedCosts: "seedCosts",
  seedAmount: "seedAmount",
  fodderLosses: "fodderLosses",
  mouldLosses: "mouldLosses",
  tillageId: "tillage",
  residue: "residue",
  irrigation: "irrigation",
  coverCropId: "coverCropType",
  legumeShare: "legumeShare",
  ccCost: "ccCost",
};

export interface PracticeOption {
  value: string;
  labelKey: string;
}

export const COVER_CROP_TYPE_OPTIONS: PracticeOption[] = [
  { value: COVER_CROP_MIX_TYPE.NONE, labelKey: "scenarioBuilder.none" },
  { value: COVER_CROP_MIX_TYPE.SUMMER_SINGLE_SPECIES, labelKey: "cropDetailsDrawer.coverCropType.singleSpeciesSummer" },
  { value: COVER_CROP_MIX_TYPE.SUMMER_MULTI_SPECIES, labelKey: "cropDetailsDrawer.coverCropType.multiSpeciesSummer" },
  { value: COVER_CROP_MIX_TYPE.WINTER_SINGLE_SPECIES, labelKey: "cropDetailsDrawer.coverCropType.singleSpeciesWinter" },
  { value: COVER_CROP_MIX_TYPE.WINTER_MULTI_SPECIES, labelKey: "cropDetailsDrawer.coverCropType.multiSpeciesWinter" },
];

export const TILLAGE_OPTIONS: PracticeOption[] = [
  { value: SOIL_SIGHT_TILLAGE.CONVENTIONAL, labelKey: "cropDetailsDrawer.tillage.conventional" },
  { value: SOIL_SIGHT_TILLAGE.REDUCED, labelKey: "cropDetailsDrawer.tillage.reduced" },
  { value: SOIL_SIGHT_TILLAGE.NONE, labelKey: "cropDetailsDrawer.tillage.noTill" },
];

export const RESIDUE_OPTIONS: PracticeOption[] = [
  { value: "true", labelKey: "cropDetailsDrawer.residue.incorporated" },
  { value: "false", labelKey: "cropDetailsDrawer.residue.removed" },
];

export const IRRIGATION_OPTIONS: PracticeOption[] = [
  { value: "false", labelKey: "cropDetailsDrawer.irrigation.rainfed" },
  { value: "true", labelKey: "cropDetailsDrawer.irrigation.irrigated" },
];

export const TIMELINE_VALUES = [10, 15, 20] as const;

export type TimelineOption = (typeof TIMELINE_VALUES)[number];

export const SCENARIO_LIMITS = {
  MIN_YEARS: 1,
  MAX_YEARS: 10,
  MAX_SCENARIOS: 5,
};

export const ECONOMICS_FIELDS: ReadonlyArray<keyof SoilSightCropDefaults> = [
  "yield",
  "sellingPrice",
  "fodderLosses",
  "mouldLosses",
  "minFertiliser",
  "plantProtection",
  "seedCosts",
  "seedAmount",
];

/** Translation keys by practice id. */
export type PracticeLabelKeys = Record<string, string>;

export const TILLAGE_LABELS: PracticeLabelKeys = Object.fromEntries(TILLAGE_OPTIONS.map(({ value, labelKey }) => [value, labelKey]));

export const RESIDUE_LABELS: PracticeLabelKeys = Object.fromEntries(RESIDUE_OPTIONS.map(({ value, labelKey }) => [value, labelKey]));

export const IRRIGATION_LABELS: PracticeLabelKeys = Object.fromEntries(IRRIGATION_OPTIONS.map(({ value, labelKey }) => [value, labelKey]));

export const COVER_CROP_LABELS: PracticeLabelKeys = Object.fromEntries(
  COVER_CROP_TYPE_OPTIONS.filter(({ value }) => value !== COVER_CROP_MIX_TYPE.NONE).map(({ value, labelKey }) => [value, labelKey])
);

export const COVER_CROP_FIT_STATUS = {
  FITS: "fits",
  WONT_FIT: "wont_fit",
} as const;

// Silver's reasons for skipping a requested cover crop; "no gap at all" is a too-short window.
export const COVER_CROP_FIT_REASON = {
  SOWING_WINDOW_CLOSED: "sowing_window_closed",
  SHORT_WINDOW: "short_window",
} as const;

export type CoverCropFitReason = (typeof COVER_CROP_FIT_REASON)[keyof typeof COVER_CROP_FIT_REASON];
